import { useEffect, useRef, useState } from "react";

const symbolism = [
  {
    icon: "ri-sword-line",
    title: "Los Arcángeles y la Pasión",
    desc: "Gabriel y Miguel sostienen la lanza, la esponja y los clavos. No son símbolos de muerte, sino de una victoria que viene después del sufrimiento.",
  },
  {
    icon: "ri-footprint-line",
    title: "La Sandalia Desatada",
    desc: "El Niño Jesús corre tan rápido a los brazos de su madre que se le suelta una sandalia. Representa el miedo real de Cristo — el mismo miedo que sintió Carlos en su cambuche.",
  },
  {
    icon: "ri-eye-line",
    title: "La Mirada de la Virgen",
    desc: "A diferencia de otras imágenes, la Virgen no mira al Niño, sino directamente al espectador. Es una mirada de consuelo que dice: \"Yo sé por lo que estás pasando\".",
  },
  {
    icon: "ri-hand-heart-line",
    title: "Las Manos Entrelazadas",
    desc: "La mano de la Virgen sostiene las de Jesús, pero mantiene su mano abierta hacia nosotros, invitándonos a poner nuestras penas en ella.",
  },
];

const timeline = [
  {
    num: "01",
    period: "Siglos XIV–XV",
    title: "Un Ícono Bizantino de Creta",
    desc: "La tabla de nogal fue pintada en la isla de Creta. Durante una travesía hacia Roma, una tempestad casi destruyó el barco; el comerciante mostró el icono y el mar se calmó, marcando el inicio de su fama como \"auxilio en el peligro\".",
  },
  {
    num: "02",
    period: "1499",
    title: "La Revelación a través de una Niña",
    desc: "La Virgen se le apareció a la hija pequeña de la familia que custodiaba el cuadro: \"Santa María del Perpetuo Socorro os requiere que la saquéis de vuestra casa\". El icono fue trasladado a la Iglesia de San Mateo, donde permaneció 300 años.",
  },
  {
    num: "03",
    period: "1798",
    title: "El Olvido Forzado",
    desc: "Las tropas de Napoleón destruyeron la iglesia de San Mateo. Los monjes agustinos rescataron el cuadro y lo ocultaron en una capilla privada. Con el paso de las décadas y la muerte de los monjes, el paradero del icono se perdió.",
  },
  {
    num: "04",
    period: "1866",
    title: "El Milagroso Redescubrimiento",
    desc: "Los Misioneros Redentoristas descubrieron por relatos antiguos que el icono debía estar en algún lugar. El Papa Pío IX, al entregárselo, les dio la misión: \"Dadla a conocer a todo el mundo\".",
  },
];

export default function VirgenSection() {
  const [visible, setVisible] = useState(false);
  const [activeSymbol, setActiveSymbol] = useState<number | null>(null);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="virgen"
      ref={ref}
      className="py-28 md:py-40 relative overflow-hidden"
      style={{ background: "linear-gradient(160deg, #fdf8ef 0%, #fef9f0 50%, #fdf6e8 100%)" }}
    >
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <img
          src="https://public.readdy.ai/ai/img_res/4f1da808ca7ca7fa203307022387e3eb.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 w-full h-px" style={{ background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />
      <div className="absolute bottom-0 left-0 w-full h-px" style={{ background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16">

        {/* ── HEADER ── */}
        <div
          className={`text-center mb-20 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: "#D4AF37", letterSpacing: "4px" }}>
            Fe y Resiliencia
          </p>
          <h2
            className="font-bold mb-6"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2rem, 4vw, 3.2rem)",
              color: "#002D62",
              lineHeight: 1.2
            }}
          >
            La Virgen del Perpetuo Socorro
          </h2>
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="h-px w-16" style={{ background: "#D4AF37" }} />
            <div className="w-2 h-2 rounded-full" style={{ background: "#D4AF37" }} />
            <div className="h-px w-16" style={{ background: "#D4AF37" }} />
          </div>
          <p
            className="max-w-2xl mx-auto"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "1.05rem",
              color: "#666",
              lineHeight: 1.8
            }}
          >
            La imagen que transformó los 67 días de agonía de Carlos Alberto en un testimonio de resiliencia y perdón. En la precariedad del cautiverio, ese icono fue su ventana hacia la libertad espiritual.
          </p>
        </div>

        {/* ── HERO: IMAGE + INTRO ── */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-28 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: "0.2s" }}
        >
          {/* Image */}
          <div className="flex justify-center">
            <div className="relative">
              {/* Outer glow ring */}
              <div
                className="absolute -inset-4 rounded-2xl opacity-30"
                style={{ background: "radial-gradient(ellipse, #D4AF37 0%, transparent 70%)" }}
              />
              {/* Frame */}
              <div
                className="relative rounded-2xl overflow-hidden"
                style={{
                  width: "340px",
                  height: "420px",
                  border: "3px solid #D4AF37",
                  boxShadow: "0 20px 60px rgba(212,175,55,0.25), 0 4px 20px rgba(0,45,98,0.15)"
                }}
              >
                <img
                  src="https://storage.readdy-site.link/project_files/951df6da-f1cc-404a-8f5e-9c6747405f5d/2d541aac-534c-4da1-816f-a0fa69782b04_Gemini_Generated_Image_bk6n9bk6n9bk6n9b.png?v=d9fa90cd64955e3824d8c8d5fa5819a8"
                  alt="Virgen del Perpetuo Socorro"
                  className="w-full h-full object-cover object-top"
                />
                {/* Subtle bottom gradient */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-24"
                  style={{ background: "linear-gradient(to top, rgba(0,45,98,0.35), transparent)" }}
                />
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <span
                    className="text-xs font-semibold tracking-widest uppercase px-4 py-1 rounded-full"
                    style={{
                      background: "rgba(212,175,55,0.9)",
                      color: "#002D62",
                      letterSpacing: "3px"
                    }}
                  >
                    Ícono Bizantino · Siglo XIV
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Intro text */}
          <div>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.35)" }}
            >
              <i className="ri-map-pin-2-line text-sm" style={{ color: "#D4AF37" }} />
              <span className="text-xs font-semibold tracking-wider uppercase" style={{ color: "#D4AF37" }}>
                Origen: Isla de Creta
              </span>
            </div>

            <h3
              className="font-bold mb-6"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.5rem, 2.5vw, 2rem)",
                color: "#002D62",
                lineHeight: 1.3
              }}
            >
              Un Ícono Bizantino de la Escuela de Creta
            </h3>

            <p
              className="mb-6 leading-relaxed"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                color: "#555",
                lineHeight: 1.85
              }}
            >
              El cuadro es una tabla de madera de nogal pintada en la isla de Creta entre los siglos XIV y XV. Es un estilo de arte &ldquo;icónico&rdquo; donde <strong style={{ color: "#002D62" }}>cada detalle tiene un propósito teológico</strong>. La leyenda cuenta que un comerciante robó la imagen de una iglesia cretense.
            </p>

            <p
              className="leading-relaxed"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                color: "#555",
                lineHeight: 1.85
              }}
            >
              Durante su travesía hacia Roma, una tempestad casi destruyó el barco; el comerciante mostró el icono a los marineros y, milagrosamente, el mar se calmó, marcando el inicio de su fama como <em style={{ color: "#002D62" }}>&ldquo;auxilio en el peligro&rdquo;</em>.
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4 mt-10">
              {[
                { value: "600+", label: "Años de historia" },
                { value: "300", label: "Años en San Mateo" },
                { value: "1866", label: "Redescubrimiento" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="text-center py-4 rounded-xl"
                  style={{ background: "rgba(0,45,98,0.05)", border: "1px solid rgba(0,45,98,0.1)" }}
                >
                  <p
                    className="font-bold"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "1.6rem",
                      color: "#D4AF37"
                    }}
                  >
                    {stat.value}
                  </p>
                  <p
                    className="text-xs mt-1"
                    style={{ fontFamily: "'Inter', sans-serif", color: "#888" }}
                  >
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── TIMELINE ── */}
        <div
          className={`mb-28 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: "0.35s" }}
        >
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: "#D4AF37", letterSpacing: "4px" }}>
              Historia
            </p>
            <h3
              className="font-bold"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                color: "#002D62"
              }}
            >
              Siglos de Misterio y Providencia
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {timeline.map((item, index) => (
              <div
                key={item.num}
                className={`relative p-8 rounded-2xl transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{
                  background: "rgba(255,255,255,0.8)",
                  border: "1px solid rgba(212,175,55,0.25)",
                  transitionDelay: `${0.4 + index * 0.1}s`
                }}
              >
                {/* Number watermark */}
                <span
                  className="absolute top-4 right-6 font-bold leading-none select-none"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "4.5rem",
                    color: "rgba(212,175,55,0.12)"
                  }}
                >
                  {item.num}
                </span>

                <div
                  className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4"
                  style={{ background: "rgba(212,175,55,0.15)", color: "#9A7B1A" }}
                >
                  {item.period}
                </div>

                <h4
                  className="font-bold mb-3"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "1.2rem",
                    color: "#002D62"
                  }}
                >
                  {item.title}
                </h4>

                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.95rem",
                    color: "#666",
                    lineHeight: 1.8
                  }}
                >
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── SYMBOLISM ── */}
        <div
          className={`transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: "0.5s" }}
        >
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: "#D4AF37", letterSpacing: "4px" }}>
              Lo que vio &ldquo;Lucho&rdquo;
            </p>
            <h3
              className="font-bold mb-4"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                color: "#002D62"
              }}
            >
              El Simbolismo de la Esperanza
            </h3>
            <p
              className="max-w-xl mx-auto"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                color: "#777",
                lineHeight: 1.7
              }}
            >
              Para una persona en cautiverio, cada detalle de esta imagen cobra un sentido de supervivencia.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {symbolism.map((item, index) => (
              <div
                key={item.title}
                className={`group p-8 rounded-2xl cursor-default transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{
                  background: activeSymbol === index ? "#002D62" : "rgba(255,255,255,0.9)",
                  border: `1px solid ${activeSymbol === index ? "#D4AF37" : "rgba(0,45,98,0.12)"}`,
                  transitionDelay: `${0.55 + index * 0.1}s`,
                  transform: activeSymbol === index ? "translateY(-4px)" : "translateY(0)"
                }}
                onMouseEnter={() => setActiveSymbol(index)}
                onMouseLeave={() => setActiveSymbol(null)}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-full mb-5"
                  style={{
                    background: activeSymbol === index ? "rgba(212,175,55,0.2)" : "rgba(0,45,98,0.07)",
                    border: `1px solid ${activeSymbol === index ? "rgba(212,175,55,0.5)" : "rgba(0,45,98,0.15)"}`
                  }}
                >
                  <i
                    className={`${item.icon} text-xl`}
                    style={{ color: activeSymbol === index ? "#D4AF37" : "#002D62" }}
                  />
                </div>

                <h4
                  className="font-bold mb-3"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "1.15rem",
                    color: activeSymbol === index ? "#FFFFFF" : "#002D62",
                    lineHeight: 1.3
                  }}
                >
                  {item.title}
                </h4>

                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.95rem",
                    color: activeSymbol === index ? "rgba(255,255,255,0.75)" : "#666",
                    lineHeight: 1.8
                  }}
                >
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Closing quote */}
          <div
            className="max-w-3xl mx-auto text-center rounded-2xl px-10 py-12 relative"
            style={{
              background: "linear-gradient(135deg, rgba(0,45,98,0.04), rgba(212,175,55,0.08))",
              border: "1px solid rgba(212,175,55,0.3)"
            }}
          >
            <span
              className="absolute -top-6 left-8 text-6xl leading-none"
              style={{ color: "#D4AF37", fontFamily: "'Playfair Display', serif" }}
            >
              &ldquo;
            </span>
            <p
              className="mb-4"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.05rem, 2vw, 1.3rem)",
                color: "#002D62",
                fontStyle: "italic",
                lineHeight: 1.75
              }}
            >
              Esta fe fue la que transformó los 67 días de agonía de Carlos Alberto en un testimonio de resiliencia y perdón. En la precariedad de su secuestro, esa imagen fue su ventana hacia la libertad espiritual.
            </p>
            <div className="flex items-center justify-center gap-3 mt-6">
              <div className="h-px w-10" style={{ background: "#D4AF37" }} />
              <i className="ri-cross-line text-sm" style={{ color: "#D4AF37" }} />
              <div className="h-px w-10" style={{ background: "#D4AF37" }} />
            </div>
            <span
              className="absolute -bottom-6 right-8 text-6xl leading-none"
              style={{ color: "#D4AF37", fontFamily: "'Playfair Display', serif" }}
            >
              &rdquo;
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
